import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import { Diagnosis } from '../utils/diagnosis';
import { whatsappUrl } from '../utils/whatsapp';
import { trackEventOnce } from '../utils/metaPixel';
import { Footer } from './Footer';
type FinalStepProps = {
  diagnosis: Diagnosis;
  onBack: () => void;
};
const deliverables = [{
  icon: '🌿',
  text: 'Una rutina de skincare personalizada según tus respuestas.'
}, {
  icon: '🧴',
  text: 'Recomendaciones de productos adaptadas a tu piel.'
}, {
  icon: '📖',
  text: 'Guía gratuita: «Los 7 errores que hacen que una crema cara no funcione».'
}, {
  icon: '💬',
  text: 'Acompañamiento directo para responder tus dudas de skincare.'
}];
export function FinalStep({
  diagnosis,
  onBack
}: FinalStepProps) {
  const link = whatsappUrl(diagnosis.skinTypeLabel, diagnosis.score);
  return <div className="min-h-[100svh] w-full bg-white" data-id="element-20" data-mp="1pce9ry77qgmd">
      <section className="mx-auto w-full max-w-2xl px-6 pb-24 pt-10 sm:px-10" data-id="element-21" data-mp="1pbml77c19j5l">
        <button type="button" onClick={onBack} className="-ml-2 flex items-center gap-2 rounded-full px-2 py-2 text-xs uppercase tracking-[0.18em] text-warmgray-400 transition-colors duration-500 hover:text-sage-700" data-id="element-22" data-mp="178mzgfbd83f0">
          <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-23" data-mp="1h00zqpb55797" />
          Volver al resultado
        </button>

        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-16 text-center text-[0.7rem] uppercase tracking-[0.32em] text-sage-600" data-id="element-24" data-mp="o993jru2silv">
          ✨ Último paso
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-8 text-center font-serif text-[2.25rem] font-light leading-[1.1] text-warmgray-800 sm:text-5xl" data-id="element-25" data-mp="136jpxw89b8wd">
          Tu diagnóstico personalizado está listo
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto mt-8 max-w-lg text-center text-base font-light leading-relaxed text-warmgray-600" data-id="element-26" data-mp="9kco4m33mh3b">
          Cada piel es única. Este diagnóstico es un excelente punto de partida,
          pero una recomendación personalizada te va a ayudar a entender
          exactamente qué rutina es la mejor para tu piel.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.35,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-16 rounded-4xl border border-beige-300 bg-beige-50 px-7 py-11 sm:px-12 sm:py-14" data-id="element-27" data-mp="4m1d4812i0ji">
          <h2 className="font-serif text-2xl font-light text-warmgray-800" data-id="element-28" data-mp="347g7q1gd538h">
            Lo que vas a recibir
          </h2>

          <ul className="mt-9 flex flex-col gap-7" data-id="element-29" data-mp="2u2zp50bi180z">
            {deliverables.map((item, index) => <motion.li key={item.text} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5 + index * 0.14,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex items-start gap-4" data-id="element-30" data-mp="2ywlwccyw8jpe" data-mpi={index}>
                <span aria-hidden="true" className="text-lg leading-none" data-id="element-31" data-mp="3htn425ju0xx1">
                  {item.icon}
                </span>
                <span className="text-sm font-light leading-relaxed text-warmgray-700 sm:text-base" data-id="element-32" data-mp="32z8pch7awao6">
                  {item.text}
                </span>
              </motion.li>)}
          </ul>

          <p className="mt-10 text-xs font-light tracking-wide text-warmgray-400" data-id="element-33" data-mp="1wgspm4u1y5vh">
            Sin compromiso de compra.
          </p>
        </motion.div>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.1,
        delay: 0.9,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto mt-24 max-w-lg text-center font-serif text-2xl font-light italic leading-relaxed text-warmgray-700 sm:text-[2rem]" data-id="element-34" data-mp="2cpi0su3lmv2k">
          Tu piel no necesita más productos.
          <br data-id="element-35" data-mp="2vlrn24onmrxn" />
          Necesita la rutina correcta.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 1.05,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-24 flex flex-col items-center" data-id="element-36" data-mp="2qnzmkm9y1xhu">
          <motion.a href={link} target="_blank" rel="noopener noreferrer" onClick={() => trackEventOnce('Contact', {
          content_name: 'WhatsApp — Diagnóstico completo',
          skin_type: diagnosis.skinTypeLabel,
          value: diagnosis.score
        })} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.99
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="w-full rounded-full bg-sage-700 px-12 py-7 text-center text-sm uppercase tracking-[0.18em] text-beige-50 transition-colors duration-700 ease-calm hover:bg-sage-800 sm:w-auto sm:px-20" data-id="element-37" data-mp="2gp0kjmjop03t">
            Recibir mi diagnóstico completo
          </motion.a>

          <p className="mt-8 max-w-sm text-center text-xs font-light leading-relaxed text-warmgray-400" data-id="element-38" data-mp="8gc8y2boz970">
            Te escribimos por WhatsApp. Sin compromiso: la recomendación es
            totalmente personalizada.
          </p>
        </motion.div>
      </section>

      <Footer data-id="element-39" data-mp="2f96dkjkeqxi2" />
    </div>;
}